import React, { Component, Fragment } from 'react'
import Mesh from 'aria-viewer/players/three/Mesh'
import * as THREE from 'three'
import { THREEx } from 'aria-viewer/legacy/video/videoTexture'
import DomEvents from '../../../utils/domevents'
import { HitCircle3D } from '../hitMesh3D'
import TriggerText3D from './TriggerText3D'
import { SpriteSheet3D } from 'aria-viewer/interactive-element/components/3d/spriteSheet3D/'
import { dispose3DObject, disposeAnimations } from '../../../utils'
import { getViewportWidth } from 'aria-viewer/utils/screenSize.js'
import TRIGGER_ASSETS from 'public/assets/png/spriteAtlases/elements_list.png'
import VISITED_CHECK from 'public/assets/png/spriteAtlases/visited_check_large.png'

import {
    Trigger3DSizes,
    Trigger3DSpeeds,
    Trigger3DRotations,
} from 'aria-viewer/interactive-element/components/3d/trigger3D/constants/'

const material = texture => {
    return new THREE.MeshBasicMaterial({
        map: texture,
    })
}

class Trigger3DBaseCircle extends Component {
    constructor(props) {
        super(props)
        // create a ref to store the textInput DOM element
        const geometry = new THREE.CircleBufferGeometry(props.radius, 64)
        this.video
        this.sceneObj
        this.rootObj
        this.mixer = {
            mesh: null,
        }
        this.clips = {}
        this.actions = {
            mesh: {},
        }
        this.clock = new THREE.Clock()
        this.allowAnimations = true
        if (props.video) {
            this.thumbnailMaterial
            this.videoMaterial
        }

        this.state = {
            isHovering: false,
            texture: null,
            material: null,
            geometry,
        }
    }

    componentDidMount() {
        const { textureResource, video, thumbnailUrl } = this.props
        const loader = new THREE.TextureLoader()
        loader.crossOrigin = 'anonymous'
        if (video) {
            this.loadVideoMaterial(textureResource)
            // Texture callback wasn't being properly triggered in safari when invoked via load function
            Promise.all([loader.load(thumbnailUrl)]).then(texture => {
                this.textureLoaded(texture)
                this.thumbnailTexture = texture
            })
        } else {
            Promise.all([loader.load(textureResource)]).then(this.textureLoaded)
        }
    }

    componentDidUpdate() {
        const { currentState } = this.props
        // Toggle between panning and default animation based on panning state
        // Use of currentlyPanning boolean prevents throttling of animation on update
        if (currentState === 'panning' && !this.currentlyPanning) {
            this.currentlyPanning = true
            this.setPanningAnimation(true)
        } else if (currentState !== 'panning' && this.currentlyPanning) {
            this.currentlyPanning = false
            this.setPanningAnimation(false)
        }
    }

    loadVideoMaterial = textureResource => {
        this.video = document.createElement('video')
        this.video.src = textureResource
        this.video.crossOrigin = 'anonymous'
        // this.video.muted = true;

        this.videoTexture = new THREEx.VideoTexture(this.video)
        this.videoTexture.minFilter = THREE.LinearFilter
        this.videoTexture.magFilter = THREE.LinearFilter
        this.videoTexture.format = THREE.RGBFormat
        this.videoTexture.needsUpdate = true
        // https://stackoverflow.com/questions/33803280/three-js-how-do-i-scaling-and-offset-my-image-textures
        this.videoTexture.repeat.set(
            Trigger3DRotations.TRIGGER_PANNING_VIDEO_TILING_REPEAT.x,
            Trigger3DRotations.TRIGGER_PANNING_VIDEO_TILING_REPEAT.y
        )
        this.videoTexture.offset.set(
            Trigger3DRotations.TRIGGER_PANNING_VIDEO_TEXTURE_OFFSET.x,
            Trigger3DRotations.TRIGGER_PANNING_VIDEO_TEXTURE_OFFSET.y
        )

        this.videoMaterial = new THREE.MeshBasicMaterial({
            map: this.videoTexture,
        })
    }

    textureLoaded = texture => {
        const { video } = this.props

        const loadedMaterial = material(texture[0])
        if (video) {
            this.thumbnailMaterial = loadedMaterial
        }
        // https://stackoverflow.com/questions/33803280/three-js-how-do-i-scaling-and-offset-my-image-textures
        var newTexture = texture[0]
        this.setState({
            texture: newTexture,
            material: loadedMaterial,
        })
    }

    onMeshMount = obj => {
        const { camera, renderer, scene, radius, isMobile } = this.props // TODO: Ideally this should be passed down by context
        // TODO: DomEvent listeners should be bound inside Mesh and not here
        this.domEvents = new DomEvents(camera, renderer.domElement)
        if (!this.rootObj) {
            this.rootObj = obj
        }
        // this.rootObj.material.depthTest = false
        // this.rootObj.renderOrder = 5

        // https://github.com/mrdoob/three.js/issues/1051
        // dynamic used for swapping materials
        const geometry = new THREE.CircleBufferGeometry(radius, 64)
        geometry.dynamic = true
        obj.geometry = geometry

        this.sceneObj = obj

        scene.add(obj)
        this.mixer.mesh = new THREE.AnimationMixer(obj)

        var expandScaleHoverKF = new THREE.VectorKeyframeTrack(
            '.scale',
            [0, Trigger3DSpeeds.TRIGGER_SCALE_HOVER_DURATION],
            Trigger3DSizes.TRIGGER_DEFAULT_SCALE_ANIM_COFACTOR.concat(
                Trigger3DSizes.TRIGGER_HOVER_TARGET_SCALE_ANIM_COFACTOR
            )
        )
        this.clips.hoverClip = new THREE.AnimationClip('hovering', Trigger3DSpeeds.TRIGGER_SCALE_HOVER_DURATION, [
            expandScaleHoverKF,
        ])
        this.actions.mesh.hoverClip = this.mixer.mesh.clipAction(this.clips.hoverClip)
        this.actions.mesh.hoverClip.loop = THREE.LoopOnce
        this.actions.mesh.hoverClip.clampWhenFinished = true

        var expandScalePanningKF = new THREE.VectorKeyframeTrack(
            '.scale',
            [0, Trigger3DSpeeds.TRIGGER_SCALE_PANNING_DURATION],
            Trigger3DSizes.TRIGGER_DEFAULT_SCALE_ANIM_COFACTOR.concat(
                Trigger3DSizes.TRIGGER_PANNING_TARGET_SCALE_ANIM_COFACTOR
            )
        )
        this.clips.panningClip = new THREE.AnimationClip('panning', Trigger3DSpeeds.TRIGGER_SCALE_PANNING_DURATION, [
            expandScalePanningKF,
        ])
        this.actions.mesh.panningClip = this.mixer.mesh.clipAction(this.clips.panningClip)
        this.actions.mesh.panningClip.loop = THREE.LoopOnce
        this.actions.mesh.panningClip.clampWhenFinished = true

        this.animate() // TODO: this should handled in the renderer animate loop
    }

    playHoverAnimation = () => {
        this.changeScaleOnHover(true)
    }

    // Takes 1 to expand, -1 to contract
    changeScaleOnHover(expansionFlag) {
        expansionFlag && this.actions.mesh.hoverClip.reset()
        if (!expansionFlag) {
            this.actions.mesh.hoverClip.paused = expansionFlag
        }
        this.actions.mesh.hoverClip.timeScale = expansionFlag ? 1 : -1
        this.actions.mesh.hoverClip.play()

        // Stop other animations after initiating anim, not before, to avoid flash back to default values before initiating delta
        this.actions.mesh.panningClip && this.actions.mesh.panningClip.stop()
    }

    playHoverExitAnimation = () => {
        clearTimeout(this.expansionTimeout)
        this.changeScaleOnHover(false)
    }

    setPanningAnimation = panningState => {
        if (!this.expanded) {
            if (panningState) {
                this.actions.mesh.panningClip.timeScale = 1
                this.actions.mesh.panningClip.paused = false
                this.actions.mesh.panningClip.play()
            } else {
                this.actions.mesh.panningClip.timeScale = -1
                this.actions.mesh.panningClip.paused = false
                this.actions.mesh.panningClip.play()
            }
            this.actions.mesh.hoverClip && this.actions.mesh.hoverClip.stop()
        }
    }

    onMeshUnmount = obj => {
        const { scene } = this.props

        this.domEvents && this.domEvents.destroy()
        this.raf && cancelAnimationFrame(this.raf)

        if (this.video) {
            this.video.pause()
            this.video.src = ''
            this.video.load()
        }

        disposeAnimations({
            mixers: this.mixer,
            actions: this.actions,
            clips: this.clips,
        })
        dispose3DObject(obj)
        scene.remove(obj)
    }

    animate = _ => {
        const { isHovering } = this.state
        const { video } = this.props
        if (!this.allowAnimations) return
        this.sceneObj.lookAt(new THREE.Vector3(0, 0, 0))

        var delta = this.clock.getDelta()
        Object.keys(this.mixer).forEach(key => {
            this.mixer[key] && this.mixer[key].update(delta)
        })

        if (video && this.videoTexture && isHovering) {
            // Something is preventing video texture from setting its own needsUpdate in legacy panoplayer context
            // This is work-around
            this.videoTexture.offset.x += Trigger3DSpeeds.TRIGGER_HOVER_VIDEO_ROTATION_SPEED
            this.videoTexture.needsUpdate = true
        }

        this.raf = requestAnimationFrame(this.animate)
    }

    handleClick = e => {
        const { onClick, mouseOutCallback, video } = this.props
        // Exits hover for both reflection and image/video sphere
        this.playHoverExitAnimation()

        // Stops video playback if video trigger
        if (video) {
            this.video.pause()
            this.video.currentTime = 0
            this.sceneObj.material = this.thumbnailMaterial
        }

        // Propagate click if not reflection orb
        mouseOutCallback && mouseOutCallback()
        onClick && onClick(e)
    }

    handleMouseOver = e => {
        const { video, mouseOverCallback, isDeviceIOS, hasHover } = this.props
        // Check if media is video and play if so
        if (hasHover) {
            this.expansionTimeout = setTimeout(() => {
                this.setState({ isHovering: true })
                this.expanded = true
                this.playHoverAnimation()
                video && !isDeviceIOS && this.playTriggerVideo()
            }, 50)
        }
        mouseOverCallback && mouseOverCallback()
    }

    playTriggerVideo = () => {
        this.video.currentTime = 0
        this.sceneObj.material = this.videoMaterial
        this.video.play()
    }

    handleMouseOut = e => {
        const { video, mouseOutCallback } = this.props
        this.setState({ isHovering: false })
        // Check if media is video and pause if so
        const viewPortWidth = getViewportWidth()
        clearTimeout(this.expansionTimeout)
        if (viewPortWidth > 540) {
            if (this.expanded) {
                this.expanded = false
                this.playHoverExitAnimation()
                video && this.restartTriggerVideo()
            }
        }
        mouseOutCallback && mouseOutCallback()
    }

    restartTriggerVideo = () => {
        this.video.pause()
        this.video.currentTime = 0
        this.videoTexture.offset.set(0.375, 0.25)
        this.sceneObj.material = this.thumbnailMaterial
    }

    componentWillUnmount() {
        this.rootObj && dispose3DObject(this.rootObj)
        this.allowAnimations = false
        this.raf && cancelAnimationFrame(this.raf)
        clearTimeout(this.expansionTimeout)
        this.raf = null
        this.rootObj = null
    }

    render() {
        const { texture, material, geometry, isHovering } = this.state
        const { position, currentState, scene, camera, renderer, isMobile, enableGaze, ...props } = this.props
        let offsetPosition = Object.assign(position, { z: position.z + 0.1 })
        return texture && material && geometry ? (
            <Fragment>
                <Mesh
                    geometry={geometry}
                    material={material}
                    position={offsetPosition}
                    scale={props.scale}
                    onMount={this.onMeshMount}
                    onWillUnmount={this.onMeshUnmount}
                />
                {this.rootObj && (
                    <TriggerText3D
                        {...props}
                        rootObj={this.rootObj}
                        isVisible={isHovering}
                        camera={camera}
                        key={'descriptionText'}
                        triggerType={'circle'}
                    />
                )}
                {this.props.children}
                {this.rootObj && (
                    <HitCircle3D
                        rootObj={this.rootObj}
                        radius={props.radius * 1.1}
                        handleClick={this.handleClick}
                        handleTouchStart={this.handleClick}
                        handleMouseOut={this.handleMouseOut}
                        handleMouseOver={this.handleMouseOver}
                        camera={camera}
                        renderer={renderer}
                        isMobile={isMobile}
                        enableGaze={enableGaze}
                    />
                )}
                {this.rootObj && currentState === 'visited' && (
                    <Fragment>
                        <SpriteSheet3D
                            depthOffset={0}
                            renderOrder={1}
                            spriteName={'icon'}
                            spriteResource={VISITED_CHECK}
                            numberOfStates={3}
                            currentState={'persistent'}
                            rootObj={this.rootObj}
                            stateIndexMap={{
                                persistent: 2,
                            }}
                            spriteScale={props.checkScale}
                            meshPosition={props.checkOffset}
                        />
                        <SpriteSheet3D
                            depthOffset={0}
                            renderOrder={7}
                            spriteName={'icon'}
                            spriteResource={VISITED_CHECK}
                            numberOfStates={3}
                            currentState={'persistent'}
                            rootObj={this.rootObj}
                            stateIndexMap={{
                                persistent: 0,
                            }}
                            spriteScale={props.checkScale}
                            meshPosition={props.checkOffset}
                        />
                    </Fragment>
                )}
                {/* DEPTH OFFSET USED HERE BECAUSE RENDER ORDER IS PROBLEMATIC WHEN USED OVER PM */}
                {this.rootObj && (
                    <SpriteSheet3D
                        depthOffset={0.01}
                        spriteName={'bodyBlur'}
                        spriteScale={props.scale}
                        meshPosition={Trigger3DSizes.BLUR_OFFSET}
                        spriteResource={TRIGGER_ASSETS}
                        numberOfStates={3}
                        currentState={'persistent'}
                        rootObj={this.rootObj}
                        stateIndexMap={{
                            persistent: 2,
                        }}
                    />
                )}
                {this.props.children}
            </Fragment>
        ) : null
    }
}
Trigger3DBaseCircle.defaultProps = {
    handleClick: () => {
        console.log('clicked 3DTriggerBase')
    },
}

export default Trigger3DBaseCircle
